import { Readable } from 'stream'
import { DriveItem, UploadSession } from '@microsoft/microsoft-graph-types'
import { HttpVerb } from '../constants/HttpVerb'
import { fetchData, fetchDataBlob } from './MiddleTier'
import { OneDriveConstants } from '../constants/OneDrive'
import { DocumentRecord } from '../models/Document'

let driveId: string

async function getDriveId() {
    if (!driveId) {
        driveId = (await fetchData('getDrive', HttpVerb.GET)).id
    }
    if (!driveId) { throw new Error('DriveId not found') }
    return driveId
}

export async function getDirectoryId(path: string) {
    // Check is directory exists?
    // If not, create it?

    // The following line does not work. I created the directories and it still didn't work
    const result = await fetchData('getDriveItem/' + encodeURIComponent(path), HttpVerb.GET)
    console.log('getDirectoryId result', result)
    return result.id
}

export function onChunkedProgress(operation: 'Upload' | 'Download', chunkIndex: number, totalChunks: number, isComplete: boolean) {
    const percentage = Math.round((chunkIndex / totalChunks) * 100)
    const status = isComplete ? ' - Complete!' : ''
    console.log(`${operation} progress: chunk ${chunkIndex}/${totalChunks} (${percentage}%)${status}`)
}

/**
 * Interface for the result of saving a document with chunked upload
 */
export interface SaveDocumentResult {
    webUrl: string
    embedUrl: string
    itemId: string
}

/**
 * Interface for file data used in email embedding and other processing
 */
export interface FileProcessingData {
    blob: Blob
    base64: string
    mimeType: string
    filename: string
    size: number
}

/**
 * Helper function to convert blob to base64 string
 * @param blob - The blob to convert
 * @returns Promise<string> - Base64 encoded string (without data URL prefix)
 */
export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result as string
            // Remove data URL prefix (data:mime/type;base64,)
            const base64 = result.split(',')[1]
            resolve(base64)
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

type URLType = 'view' | 'edit' | 'embed'
export async function createURL(doc: DocumentRecord, type: URLType, ttl: number = 30): Promise<string> {
    switch (type) {
        case 'view':
            // return await createLinkNew(doc.ItemId, 'view', 'anonymous', ttl)
            return await createLink(doc.ItemId!, doc.Filename!, 'view')
        case 'edit':
            if (!doc.ItemId && doc.EditURL) return doc.EditURL
            // return await createLinkNew(doc.ItemId, 'edit', 'organization', ttl)
            return await createLink(doc.ItemId!, doc.Filename!, 'edit')
        case 'embed':
            return await previewLink(doc.ItemId!)
    }
}

export async function createLink(itemId: string, filename: string, type: string): Promise<string> {
    const link = await fetchData(`createLink/${itemId}/${encodeURIComponent(filename)}?linkType=${type}`, HttpVerb.GET)
    return link.link.webUrl
}

// https://learn.microsoft.com/en-us/graph/api/listitem-createlink?view=graph-rest-beta&viewFallbackFrom=graph-rest-1.0&tabs=http
type LinkType = 'view' | 'edit' | 'embed' | 'review' | 'blocksDownload' | 'createOnly | addressBar' | 'adminDefault'
type LinkScope = 'anonymous' | 'organization' | 'users'
// ttl: Time to Live in minutes
export async function createLinkNew(itemId: string, type: LinkType, scope: LinkScope, ttl: number): Promise<string> {
    return await createLinkFromOptions(itemId, {
        type: type,
        scope: scope,
        expirationDateTime: new Date(Date.now() + ttl * 60 * 1000).toISOString(), // ttl minutes from now
    })
}

type LinkOptions = {
    type?: LinkType
    scope?: LinkScope
    expirationDateTime?: string // ISO 8601 format, e.g. '2023-10-01T00:00:00Z'
    password?: string // optional password for the link
    recipients?: string | { email: string }[] // semicolon separated email addresses
    retainInheritedPermissions?: boolean // true to retain inherited permissions, false to remove them
    sendNotification?: boolean // true to send notification email to recipients
}
export async function createLinkFromOptions(itemId: string, options: LinkOptions): Promise<string> {
    let o = { ...options }
    if (typeof o.recipients === 'string') {
        o.recipients = o.recipients.split(';').map(e => { return { email: e.trim() } })
    }
    const body = JSON.stringify(o)
    return await createLinkFromBody(itemId, body)
}

async function createLinkFromBody(itemId: string, body: string): Promise<string> {
    // Joseph: Fuck the chicken right here!
    return ''
}

async function previewLink(itemId: string): Promise<string> {
    // Middle Tier needs to field this new call 'createPreviewLink'
    // Joseph: More F'n.
    const link = await fetchData(`createPreviewLink/${itemId}`, HttpVerb.GET)
    return link.getUrl
}

function gcPath(caseId: string, path: string) {
    return 'cases/' + caseId + '/' + path
}

export async function saveGCDocument(caseId: string, path: string, filename: string, content: Blob) {
    const oneDrivePath = gcPath(caseId, path)
    const oneDriveFilePath = oneDrivePath + '/' + filename
    const response = await saveDocument(oneDriveFilePath, content, typeForFilename(filename))
    //console.log('response', response)
    const itemId = response._responseBody?.id!
    const link = await fetchData(`createLink/${itemId}/${encodeURIComponent(filename)}?linkType=edit`, HttpVerb.GET)
    return link.link.webUrl
}

export async function saveGCDocumentChunked(caseId: string, path: string, filename: string, content: Blob): Promise<SaveDocumentResult> {
    // Handle undefined, null, or empty path
    const cleanPath = path && path.trim() ? path.trim() : ''
    const oneDrivePath = cleanPath ? `cases/${caseId}/${cleanPath}` : `cases/${caseId}`
    const oneDriveFilePath = oneDrivePath + '/' + filename

    // Convert Blob to File for chunked upload
    const file = new File([content], filename, { type: typeForFilename(filename) })
    const totalChunks = Math.ceil(file.size / OneDriveConstants.CHUNK_SIZE)

    // Progress tracking function
    function onProgress(chunkIndex: number, isComplete: boolean) {
        onChunkedProgress('Upload', chunkIndex, totalChunks, isComplete)
    }

    try {
        // Upload file using the new uploadFile function
        const fileMetadata = await uploadFile(oneDriveFilePath, file, onProgress)

        // Create link using the new createLink function
        const webUrl = await createLink(fileMetadata.id!, filename, 'edit')
        const embedUrl = "" // await createLink(fileMetadata.id!, filename, 'embed') didn't work

        return {
            webUrl: webUrl,
            embedUrl: embedUrl,
            itemId: fileMetadata.id!
        }
    } catch (error) {
        console.error('Chunked upload failed:', error)
        throw error
    }
}

export async function saveDocument(filename: string, content: Blob, type: string) {
    //console.log('saveDocument', filename, type)
    return await fetchData(`uploadFile/${encodeURIComponent(filename)}?replace=true`, HttpVerb.POST, content as unknown as Readable, type)
}

export async function loadGCDocument(caseId: string, path: string, filename: string) {
    const storagePath = gcPath(caseId, path)
    const filePath = storagePath + filename
    const itemId = await getDirectoryId(filePath)
    return await loadDocument(itemId)
}

export async function loadGCDocumentChunked(caseId: string, path: string, filename: string): Promise<Blob> {
    // Handle undefined, null, or empty path
    const cleanPath = path && path.trim() ? path.trim() : ''
    const storagePath = cleanPath ? `cases/${caseId}/${cleanPath}` : `cases/${caseId}`
    const filePath = storagePath + filename
    const itemId = await getDirectoryId(filePath)
    return await loadGCDocumentChunkedId(itemId)
}

export async function loadGCDocumentChunkedId(itemId: string): Promise<Blob> {

    // Progress tracking function
    function onProgress(chunkIndex: number, isComplete: boolean) {
        // We don't know total chunks here, so we'll let downloadFile handle the actual progress
        // This is just a pass-through for the onChunkedProgress function
        onChunkedProgress('Download', chunkIndex, chunkIndex, isComplete)
    }

    try {
        // Download file using the new downloadFile function
        const fileBlob = await downloadFile(itemId, onProgress)
        console.log(`📦 File blob returned: ${fileBlob.size} bytes`)

        // Always return the blob
        return fileBlob
    } catch (error) {
        console.error('Chunked download failed:', error)
        throw error
    }
}

export async function loadDocument(itemId: string) {
    const item = await fetchDataBlob(`downloadFile/${itemId}`, HttpVerb.GET)
    return item
}

export function typeForFilename(filename: string): string {
    const extension = filename.split('.').pop()
    switch (extension) {
        case 'doc':
            return 'application/msword'
        case 'docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        case 'gif':
            return 'image/gif'
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg'
        case 'png':
            return 'image/png'
        case 'tif':
        case 'tiff':
            return 'image/tiff'
        case 'pdf':
            return 'application/pdf'
        case 'txt':
            return 'text/plain'
        case 'zip':
            return 'application/zip'
        case 'xls':
            return 'application/vnd.ms-excel'
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        default:
            return 'application/octet-stream'
    }
}

/**
 * Helper function to decode base64 data from a blob
 * @param blob - The blob containing base64 encoded data
 * @returns Promise<Blob> - A new blob with the decoded binary data
 */
async function decodeBase64Blob(blob: Blob): Promise<Blob> {
    const text = await blob.text()
    const binaryString = atob(text)
    const bytes = new Uint8Array(binaryString.length)

    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }

    return new Blob([bytes])
}

//
// The following uploadFile & downloadFile methods make use of the methods below it which
// make the actual calls to the REST API to perform the work. These functions do things like
// break the file being uploaded into chunks so each chunk can be sent. On download the chunks
// are then reassembled into a Blob. These functions are currently being called from the Test
// page to exercise / proof they work. Please click the upload file button then the download file
// button and you can see the network requests across the wire working.
//

export async function uploadFile(filePath: string, file: File, onProgress?: (chunkIndex: number, isComplete: boolean) => void): Promise<DriveItem> {
    const chunkSize = OneDriveConstants.CHUNK_SIZE
    const totalSize = file.size
    const totalChunks = Math.ceil(totalSize / chunkSize)

    try {
        // Step 1: Start upload session
        const session = await uploadFileStart(filePath, totalSize)

        // Step 2: Prepare all chunks concurrently (this is the performance improvement)
        console.log(`📦 Preparing ${totalChunks} chunks concurrently...`)
        const chunkPromises: Promise<ArrayBuffer>[] = []

        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize, totalSize)
            chunkPromises.push(file.slice(start, end).arrayBuffer())
        }

        // Wait for all chunks to be prepared concurrently
        const chunkBuffers = await Promise.all(chunkPromises)
        console.log(`✅ All ${totalChunks} chunks prepared concurrently`)

        // Step 3: Upload chunks sequentially (OneDrive requires this)
        let uploadComplete = false
        let fileMetadata: DriveItem | null = null

        for (let i = 0; i < totalChunks && !uploadComplete; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize, totalSize)
            const chunkData = chunkBuffers[i] // Use pre-prepared chunk

            onProgress?.(i + 1, false)

            const result = await uploadFileChunk(
                session.uploadUrl!,
                chunkData,
                start,
                end - 1,
                totalSize
            )

            // Check if upload completed with this chunk
            if (result && typeof result === 'object' && result.id) {
                fileMetadata = result as DriveItem
                uploadComplete = true
                onProgress?.(i + 1, true)
            }
        }

        if (!uploadComplete || !fileMetadata) {
            throw new Error('Upload completed but no file metadata received')
        }

        return fileMetadata

    } catch (error) {
        console.error('Chunked upload failed:', error)
        throw error
    }
}

export async function downloadFile(itemId: string, onProgress?: (chunkIndex: number, isComplete: boolean) => void): Promise<Blob> {
    const chunkSize = OneDriveConstants.CHUNK_SIZE
    const maxConcurrentDownloads = 5 // Limit concurrent downloads to avoid overwhelming server

    try {
        // Step 1: Get file info
        const fileInfo = await downloadFileInfo(itemId)
        const totalSize = fileInfo.size!
        const totalChunks = Math.ceil(totalSize / chunkSize)

        console.log(`📦 Starting concurrent download of ${totalChunks} chunks...`)

        // Step 2: Create download promises for all chunks
        const chunkPromises: Promise<{ index: number; blob: Blob }>[] = []

        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize - 1, totalSize - 1)

            const chunkPromise = downloadFileChunk(itemId, start, end).then(blob => ({
                index: i,
                blob: blob
            }))

            chunkPromises.push(chunkPromise)
        }

        // Step 3: Download chunks with controlled concurrency
        const chunks: Blob[] = new Array(totalChunks)
        let completedChunks = 0

        // Process chunks in batches to control concurrency
        for (let i = 0; i < chunkPromises.length; i += maxConcurrentDownloads) {
            const batch = chunkPromises.slice(i, i + maxConcurrentDownloads)

            const batchResults = await Promise.all(batch)

            // Place chunks in correct order
            for (const result of batchResults) {
                chunks[result.index] = result.blob
                completedChunks++
                onProgress?.(completedChunks, false)
                console.log(`⬇️ Downloaded chunk ${result.index + 1}/${totalChunks} (${completedChunks} completed)`)
            }
        }

        console.log(`✅ All ${totalChunks} chunks downloaded concurrently`)

        // Step 4: Reassemble file
        const completeFile = new Blob(chunks, { type: fileInfo.file?.mimeType || 'application/octet-stream' })

        onProgress?.(totalChunks, true)

        return completeFile
    } catch (error) {
        console.error('Chunked download failed:', error)
        throw error
    }
}

//
// These are convenience methods to call the chunked file operations via fetch. Remember to keep the chunked at
// or below 4MB due to the max lambda request payload size of 6MB due to base64 inflating the total size by ~30%.
// This includes the size of the base64 encoding which can exceed 30% of the total size.
//

/**
 * This function downloads info about a file, such as its size.
 * 
 * Argument @itemId: The DriveItem ID of the file.
 * Returns: DriveItem
 */
export async function downloadFileInfo(itemId: string): Promise<DriveItem> {
    return await fetchData(`downloadFileInfo/${itemId}`, HttpVerb.GET)
}

/**
 * This function downloads a file. If you want to download a folder
 * you'll need to list the folder and download each file in it.
 * 
 * Argument @itemId: The DriveItem ID of the file.
 * Returns: Blob
 */
export async function downloadFileChunk(itemId: string, start: number, end: number): Promise<Blob> {
    // Get the response as blob (which will contain the base64 text)
    const blob = await fetchDataBlob(`downloadFileChunk/${itemId}?start=${start}&end=${end}`, HttpVerb.GET)

    // Convert blob to text to get the base64 string
    const base64Text = await blob.text()

    // Decode the base64 string to binary data
    const binaryString = atob(base64Text)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }

    return new Blob([bytes])
}

/**
 * This function marks the beginning of an upload session. If this
 * is called and uploading fails you MUST cancel the session.
 * 
 * Argument @itemId: The DriveItem ID of the file.
 * Argument @totalSize: The total size of the file.
 * Returns: DriveItem
 */
export async function uploadFileStart(filePath: string, totalSize: number): Promise<UploadSession> {
    return await fetchData(`uploadFileStart/${encodeURIComponent(filePath)}?totalSize=${totalSize}`, HttpVerb.POST)
}

/**
 * This function uploads a file. If you want to upload a folder
 * you'll need to list the folder locally and upload each file from it.
 * 
 * Argument @uploadUrl: The DriveItem ID of the file.
 * Argument @chunkData: The DriveItem ID of the file.
 * Argument @start: The 0 based start of the chunk.
 * Argument @end: The 0 based end of the chunk.
 * Argument @totalSize: The total size of the file.
 * Returns: Blob
 */
export async function uploadFileChunk(uploadUrl: string, chunkData: ArrayBuffer, start: number, end: number, totalSize: number) {
    const encodedUploadUrl = encodeURIComponent(uploadUrl)
    const path = `uploadFileChunk?uploadUrl=${encodedUploadUrl}&start=${start}&end=${end}&totalSize=${totalSize}`
    return await fetchData(path, HttpVerb.PUT, chunkData, 'application/octet-stream')
}

/**
 * This function cancels a file upload.
 * 
 * Argument @uploadUrl: The DriveItem ID of the file.
 * Returns: DriveItem
 */
export async function uploadFileCancel(uploadUrl: string) {
    const encodedUploadUrl = encodeURIComponent(uploadUrl)
    return await fetchData(`uploadFileCancel?uploadUrl=${encodedUploadUrl}`, HttpVerb.POST)
}

